import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Download, Mail, FileText, Search, ChevronLeft, ChevronRight, TrendingUp, Clock, Filter, Calendar } from 'lucide-react';
import { exportToCSV } from '../utils/csvExport';
import { fetchReports, generatePdf, getPdfUrl, emailPdf, getReportsAnalytics } from '../api/reports';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import StatCard from '../components/charts/StatCard';
import PieChart from '../components/charts/PieChart';
import TrendChart from '../components/charts/TrendChart';
import { auth } from '../firebase/config';
import toast from 'react-hot-toast';

const TEST_TYPE_LABELS = {
  bigfive: 'Big Five',
  productivity: 'Productivity',
  extended: 'Extended',
  advanced: 'Advanced',
  edutech: 'Edutech'
};

const TEST_TYPE_BADGE_CLASSES = {
  bigfive: 'bg-indigo-100 text-indigo-800',
  productivity: 'bg-emerald-100 text-emerald-800',
  extended: 'bg-purple-100 text-purple-800',
  advanced: 'bg-cyan-100 text-cyan-800',
  edutech: 'bg-blue-100 text-blue-800'
};

const getTestTypeLabel = (testType) => TEST_TYPE_LABELS[testType] || testType || 'Unknown';
const getTestTypeBadgeClass = (testType) =>
  TEST_TYPE_BADGE_CLASSES[testType] || 'bg-slate-100 text-slate-800';

const ReportsPageEnhanced = () => {
  const [reports, setReports] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [actionLoading, setActionLoading] = useState({});
  const [selectedReports, setSelectedReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Cache key for PDF generation status
  const PDF_CACHE_KEY = 'pdf_generation_cache';

  // Load cached PDF status
  const loadPdfCache = () => {
    try {
      const cached = localStorage.getItem(PDF_CACHE_KEY);
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  };

  // Save PDF status to cache
  const savePdfCache = (attemptId, status) => {
    try {
      const cache = loadPdfCache();
      cache[attemptId] = { hasPdf: status, timestamp: Date.now() };
      localStorage.setItem(PDF_CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
      console.error('Failed to cache PDF status:', e);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await getReportsAnalytics();
      setAnalytics(response.data || response);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const loadReports = useCallback(async (searchTerm = search, currentPage = page, currentPageSize = pageSize) => {
    try {
      setLoading(true);
      const response = await fetchReports({
        page: currentPage,
        pageSize: currentPageSize,
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        startDate: dateRange.start || undefined,
        endDate: dateRange.end || undefined
      });
      
      // Merge with cached PDF status
      const cache = loadPdfCache();
      const mergedReports = response.data.map(report => {
        const attemptId = report.attempt_id || report.attemptId;
        const cached = cache[attemptId];
        // Use cached status if available and recent (within 1 hour)
        if (cached && (Date.now() - cached.timestamp < 3600000)) {
          return { ...report, hasPdf: cached.hasPdf };
        }
        return report;
      });
      
      setReports(mergedReports);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Failed to load reports:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load reports');
      }
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize]);

  useEffect(() => {
    loadReports();
  }, [loadReports, statusFilter, dateRange]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (search !== '') {
        setPage(1);
        loadReports(search, 1);
      } else {
        loadReports('', page);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [search]);

  const handleAction = async (action, attemptId, reportIndex) => {
    setActionLoading(prev => ({ ...prev, [`${action}-${attemptId}`]: true }));
    
    try {
      switch (action) {
        case 'regenerate':
          await generatePdf(attemptId);
          toast.success('PDF regenerated successfully');
          savePdfCache(attemptId, true);
          setReports(prev => prev.map((report, index) => 
            index === reportIndex 
              ? { ...report, hasPdf: true, pdfGeneratedAt: new Date().toISOString() }
              : report
          ));
          loadAnalytics();
          break;
          
        case 'download':
          // Fetch PDF file with authentication
          const token = await auth.currentUser?.getIdToken();
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/reports/${attemptId}/file`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (!response.ok) {
            if (response.status === 404) {
              toast.error('PDF not found. Please regenerate it first.');
            } else {
              toast.error('Failed to download PDF');
            }
            break;
          }
          
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `report_${attemptId}.pdf`;
          link.click();
          toast.success('PDF downloaded');
          
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
          break;
          
        case 'email':
          const emailResponse = await emailPdf(attemptId);
          const sentTo = emailResponse.data?.email || emailResponse.email;
          toast.success(`Email sent to ${sentTo}`);
          break;
          
        case 'generate':
          await generatePdf(attemptId);
          toast.success('PDF generated successfully');
          // Cache the PDF generation status
          savePdfCache(attemptId, true);
          setReports(prev => prev.map((report, index) => 
            index === reportIndex 
              ? { ...report, hasPdf: true, pdfGeneratedAt: new Date().toISOString() }
              : report
          ));
          loadAnalytics();
          break;
      }
    } catch (error) {
      console.error(`${action} action failed:`, error);
      const errorMsg = error.response?.data?.error || `Failed to ${action} report`;
      toast.error(errorMsg);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${action}-${attemptId}`]: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadReports(search, newPage);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedReports(reports.map(r => r.attempt_id || r.attemptId));
    } else {
      setSelectedReports([]);
    }
  };

  const handleSelectReport = (attemptId) => {
    setSelectedReports(prev => 
      prev.includes(attemptId) 
        ? prev.filter(id => id !== attemptId)
        : [...prev, attemptId]
    );
  };

  const handleBulkAction = async (action) => {
    if (selectedReports.length === 0) {
      toast.error('Please select reports first');
      return;
    }
    
    const confirmMsg = `${action} ${selectedReports.length} report(s)? This may take a few minutes.`;
    if (!confirm(confirmMsg)) return;
    
    setActionLoading(prev => ({ ...prev, [`bulk-${action}`]: true }));
    
    let successCount = 0;
    let failCount = 0;
    const toastId = toast.loading(`Processing 0/${selectedReports.length}...`);
    
    try {
      // Process sequentially to avoid timeout
      for (let i = 0; i < selectedReports.length; i++) {
        const attemptId = selectedReports[i];
        try {
          toast.loading(`Processing ${i + 1}/${selectedReports.length}...`, { id: toastId });
          
          switch (action) {
            case 'generate':
              await generatePdf(attemptId);
              // Update local state to show PDF is now available
              setReports(prev => prev.map(r => 
                (r.attempt_id || r.attemptId) === attemptId 
                  ? { ...r, hasPdf: true, pdfGeneratedAt: new Date().toISOString() }
                  : r
              ));
              savePdfCache(attemptId, true);
              break;
            case 'email':
              await emailPdf(attemptId);
              break;
          }
          successCount++;
        } catch (error) {
          console.error(`Failed to ${action} report ${attemptId}:`, error);
          failCount++;
        }
      }
      
      toast.dismiss(toastId);
      if (failCount === 0) {
        toast.success(`Successfully ${action}d ${successCount} report(s)`);
      } else {
        toast.success(`Completed: ${successCount} success, ${failCount} failed`);
      }
      
      setSelectedReports([]);
      loadReports();
    } catch (error) {
      toast.dismiss(toastId);
      console.error(`Bulk ${action} failed:`, error);
      toast.error(`Bulk ${action} failed`);
    } finally {
      setActionLoading(prev => ({ ...prev, [`bulk-${action}`]: false }));
    }
  };

  const handleExportCSV = () => {
    const exportData = reports.map(r => ({
      Name: r.name || r.display_name || r.userName || 'Unknown',
      Email: r.email,
      TestType: getTestTypeLabel(r.test_type),
      CompletedAt: formatDate(r.completedAt || r.completed_at),
      HasPDF: r.hasPdf ? 'Yes' : 'No'
    }));
    exportToCSV(exportData, `reports_${new Date().toISOString().split('T')[0]}.csv`);
    toast.success('Reports exported to CSV');
  };

  // Prepare chart data
  const reportTypeData = analytics?.byType ? Object.entries(analytics.byType).map(([key, value]) => ({
    name: getTestTypeLabel(key),
    value: value
  })) : [];

  const generationTrendData = analytics?.generationTrend || [];

  if (loading && reports.length === 0 && !analytics) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Reports</h1>
        <p className="text-gray-600">View, download, and email CogniQ assessment reports.</p>
      </motion.div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <StatCard
              icon={FileText}
              label="Total Reports"
              value={analytics.totalReports || 0}
              trend={analytics.trends?.reports}
              color="blue"
            />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <StatCard
              icon={Download}
              label="Downloads (7d)"
              value={analytics.downloads7d || 0}
              trend={analytics.trends?.downloads}
              color="green"
            />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <StatCard
              icon={Mail}
              label="Emails Sent (7d)"
              value={analytics.emailsSent7d || 0}
              trend={analytics.trends?.emails}
              color="purple"
            />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <StatCard
              icon={Clock}
              label="Avg Gen Time"
              value={`${analytics.avgGenerationTime || 0}s`}
              trend={analytics.trends?.genTime}
              color="orange"
            />
          </motion.div>
        </div>
      )}

      {/* Charts Row */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Reports by Type */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Reports by Type</h3>
                <div className="flex items-center justify-center">
                  {reportTypeData.length > 0 ? (
                    <div className="w-full max-w-sm">
                      <PieChart 
                        data={reportTypeData}
                        colors={['#3b82f6', '#8b5cf6', '#10b981']}
                        height={240}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-gray-500">
                      No data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Generation Trend */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Generation Trend (7 Days)
                </h3>
                {generationTrendData.length > 0 ? (
                  <TrendChart
                    data={generationTrendData}
                    dataKeys={[{ dataKey: 'count', name: 'Reports Generated' }]}
                    colors={['#3b82f6']}
                    type="area"
                    height={240}
                  />
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Search and Controls */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="w-full sm:flex-1">
                <div className="relative w-full max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Show:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const newPageSize = parseInt(e.target.value);
                      setPageSize(newPageSize);
                      setPage(1);
                      loadReports(search, 1, newPageSize);
                    }}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <div className="text-sm text-gray-500">
                  {total} total
                </div>
              </div>
            </div>
            
            {/* Filters Row */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="has_pdf">Has PDF</option>
                  <option value="no_pdf">No PDF</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="Start date"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="End date"
                />
              </div>
              
              {selectedReports.length > 0 && (
                <div className="flex items-center space-x-2 ml-auto">
                  <span className="text-sm text-gray-600">{selectedReports.length} selected</span>
                  <Button size="sm" onClick={() => handleBulkAction('generate')} loading={actionLoading['bulk-generate']}>
                    Bulk Generate
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleBulkAction('email')} loading={actionLoading['bulk-email']}>
                    Bulk Email
                  </Button>
                </div>
              )}
              
              <Button size="sm" variant="outline" onClick={handleExportCSV} className="ml-auto">
                <Download className="w-4 h-4 mr-1" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedReports.length === reports.length && reports.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Test Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Completed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reports.map((report, index) => (
                  <motion.tr 
                    key={report.attempt_id || report.attemptId}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedReports.includes(report.attempt_id || report.attemptId)}
                        onChange={() => handleSelectReport(report.attempt_id || report.attemptId)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {report.name || report.display_name || report.userName || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTestTypeBadgeClass(report.test_type)}`}>
                        {getTestTypeLabel(report.test_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(report.completedAt || report.completed_at || report.createdAt || report.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {report.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {report.hasPdf ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAction('regenerate', report.attempt_id || report.attemptId, index)}
                              loading={actionLoading[`regenerate-${report.attempt_id || report.attemptId}`]}
                              className="flex items-center space-x-1"
                              title="Regenerate PDF"
                            >
                              <RefreshCw className="w-4 h-4" />
                              <span>Regenerate</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAction('download', report.attempt_id || report.attemptId, index)}
                              loading={actionLoading[`download-${report.attempt_id || report.attemptId}`]}
                              className="flex items-center space-x-1"
                            >
                              <Download className="w-4 h-4" />
                              <span>Download</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAction('email', report.attempt_id || report.attemptId, index)}
                              loading={actionLoading[`email-${report.attempt_id || report.attemptId}`]}
                              className="flex items-center space-x-1"
                            >
                              <Mail className="w-4 h-4" />
                              <span>Email</span>
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleAction('generate', report.attempt_id || report.attemptId, index)}
                            loading={actionLoading[`generate-${report.attempt_id || report.attemptId}`]}
                            className="flex items-center space-x-1"
                          >
                            <FileText className="w-4 h-4" />
                            <span>Generate PDF</span>
                          </Button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {reports.length === 0 && !loading && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No reports found</h3>
              <p className="text-gray-500">
                {search ? 'Try adjusting your search criteria.' : 'No completed assessments available yet.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {page} of {totalPages} ({total} total)
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="flex items-center space-x-1"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              className="flex items-center space-x-1"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPageEnhanced;
